/**
 * URL and route redaction for analytics hygiene and privacy compliance.
 * Pure TypeScript — no DOM or React/React Native dependencies.
 */

const UUID_REGEX_TEST = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;
const UUID_REGEX_GLOBAL = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;

const ALLOWED_QUERY_PARAMS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'ref',
  'qr',
]);

/**
 * Normalizes route pathname by replacing dynamic tokens, codes, and UUIDs with placeholders.
 * e.g. /review/abc123xyz -> /review/:token
 *      /quote/abc123xyz/pay -> /quote/:token/pay
 *      /r/promo1 -> /r/:code
 *      /dashboard/jobs/6f1c2a9e-3b4d-4e5f-8a7b-9c0d1e2f3a4b -> /dashboard/jobs/:id
 */
export function normalizeRoute(pathname: string): string {
  if (!pathname) return '/';

  const rawPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const segments = rawPath.split('/').filter(Boolean);

  if (segments.length === 0) return '/';

  let normalized: string;

  // Handle /quote/:token subroutes
  if (segments[0] === 'quote' && segments.length >= 2) {
    if (segments[1] === 'pay') {
      normalized = '/quote/pay';
    } else if (segments[2] === 'pay') {
      normalized = '/quote/:token/pay';
    } else if (segments[2] === 'receipt') {
      normalized = '/quote/:token/receipt';
    } else {
      normalized = '/quote/:token';
    }
  } else if (segments[0] === 'warranty' && segments[1] === 'report' && segments.length >= 3) {
    normalized = '/warranty/report/:token';
  } else if (segments[0] === 'jobs' && segments[1] === 'teasers' && segments.length >= 3) {
    normalized = '/jobs/teasers/:token';
  } else if (segments[0] === 'supplier' && segments[1] === 'verify' && segments.length >= 3) {
    normalized = '/supplier/verify/:token';
  } else if (
    ['review', 'reschedule', 'schedule', 'sign-variation', 'sign-job', 'upload'].includes(segments[0]) &&
    segments.length >= 2
  ) {
    normalized = `/${segments[0]}/:token`;
  } else if (['r', 's', 'qr'].includes(segments[0]) && segments.length >= 2) {
    normalized = `/${segments[0]}/:code`;
  } else {
    // General path — replace any segment that is a UUID with :id
    normalized = '/' + segments.map((seg) => (UUID_REGEX_TEST.test(seg) ? ':id' : seg)).join('/');
  }

  // Replace any remaining UUIDs anywhere in the path (e.g. nested subpaths)
  normalized = normalized.replace(UUID_REGEX_GLOBAL, ':id');

  return normalized || '/';
}

/**
 * Redacts a URL (absolute or relative) by:
 * - normalising route path tokens and UUIDs
 * - keeping ONLY approved attribution query parameters (utm_*, gclid, fbclid, etc.)
 * - stripping all other query parameters (email, token, access_token, etc.)
 * - ALWAYS dropping the hash fragment
 */
export function redactUrl(rawUrl: string): string {
  if (!rawUrl) return '';

  try {
    const isAbsolute = /^https?:\/\//i.test(rawUrl);
    const dummyBase = 'https://tradeconnector.local';
    const parsed = new URL(rawUrl, dummyBase);

    const redactedPath = normalizeRoute(parsed.pathname);

    // Keep ONLY approved query params
    const filteredParams = new URLSearchParams();
    for (const [key, value] of parsed.searchParams.entries()) {
      if (ALLOWED_QUERY_PARAMS.has(key.toLowerCase())) {
        filteredParams.append(key, value);
      }
    }

    const queryString = filteredParams.toString();
    const search = queryString ? `?${queryString}` : '';

    if (isAbsolute) {
      return `${parsed.origin}${redactedPath}${search}`;
    }
    return `${redactedPath}${search}`;
  } catch {
    return '';
  }
}
